import { CheckIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Icon,
  Input,
  Switch,
  Text
} from "@chakra-ui/react";
import { useCallback, useReducer, useState } from "react";
import { GetLayout } from "components/get-layout";
import { Toast } from "components/toast";
import { ModelosPipelineEndpoints, PipelineModeloDTO } from "./utils";
import { StatesMachine } from "components/pipeline-components/classes/state-machine/state-machine";
import { StateProps } from "components/pipeline-components/classes/state-machine/state";
import { canInsertOperation } from "components/pipeline-components/pipeline-commands";
import { FormState } from "./types";
import {
  initModeloMachine,
  modeloMachineReducer,
  getBlocksFromMachine
} from "./modelo-machine-reducer";
import { COLOR_OPTIONS, ICON_OPTIONS } from "./constants";
import { BLOCKS_BY_OPERATION } from "components/pipeline-components/node-catalog";
import { mailingConveniosOptions } from "components/mailing-components/mailing-modal-filter/consts";
import { dropdownTipoOperacaoMailing } from "components/mailing-components/mailing-upload-modal/consts";
import { SectionCard } from "./section-card";
import { TagToggle } from "./tag-toggle";
import { BlockSequencePanel } from "./block-sequence-panel";
import { TemplatePreviewPanel } from "./template-preview-panel";

export function ModeloFormView({
  editingModelo,
  onBack,
  onSaved
}: {
  editingModelo?: PipelineModeloDTO;
  onBack: () => void;
  onSaved: () => void;
}) {
  const isEditing = !!editingModelo?.id;
  const [form, setForm] = useState<FormState>(() => ({
    nome: editingModelo?.nome ?? "",
    icone: editingModelo?.icone ?? "zap",
    cor: editingModelo?.cor ?? "#2F80ED",
    convenio: editingModelo?.convenio ?? "",
    tipoOperacao: editingModelo?.tipoOperacao ?? "",
    ativo: editingModelo?.status !== "INATIVO"
  }));
  const [machineState, dispatchMachine] = useReducer(
    modeloMachineReducer,
    editingModelo?.sequenciaBloco,
    initModeloMachine
  );
  const [expandedOps, setExpandedOps] = useState<Set<StateProps["operation"]>>(
    new Set()
  );
  const blocks = getBlocksFromMachine(machineState, expandedOps);
  const descricaoCurta =
    machineState.orderedOps.length > 0
      ? machineState.orderedOps
          .map((op) => BLOCKS_BY_OPERATION[op]?.label ?? op)
          .join(", ")
      : "";
  const [isSaving, setIsSaving] = useState(false);

  const setField = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const updateMachineState = useCallback(
    (
      updater: (draft: StatesMachine) => StatesMachine | void,
      orderedOps?: StateProps["operation"][]
    ) => {
      dispatchMachine({ type: "MUTATE", updater, orderedOps });
    },
    [dispatchMachine]
  );

  const toggleField = (field: "convenio" | "tipoOperacao", value: string) => {
    setField(field, form[field] === value ? "" : value);
  };

  const isMultiActive = (field: "convenio" | "tipoOperacao", value: string) =>
    form[field] === value;

  const addBlock = useCallback(
    (op: StateProps["operation"]) => {
      if (!canInsertOperation(machineState.machine, op)) return;

      const nextOps = [...machineState.orderedOps, op];
      updateMachineState((draft) => {
        draft.addOperationState(op);
      }, nextOps);
    },
    [machineState, updateMachineState]
  );

  const removeBlock = useCallback(
    (index: number) => {
      const op = machineState.orderedOps[index];
      if (!op) return;

      const nextOps = machineState.orderedOps.filter((_, i) => i !== index);
      updateMachineState((draft) => {
        draft.removeOperation(op);
      }, nextOps);
    },
    [machineState, updateMachineState]
  );

  const reorderBlocks = useCallback(
    (from: number, to: number) => {
      if (
        from === to ||
        from < 0 ||
        to < 0 ||
        from >= machineState.orderedOps.length ||
        to >= machineState.orderedOps.length
      ) {
        return;
      }

      const nextOps = [...machineState.orderedOps];
      const [item] = nextOps.splice(from, 1);
      if (!item) return;
      nextOps.splice(to, 0, item);

      updateMachineState(() => undefined, nextOps);
    },
    [machineState, updateMachineState]
  );

  const updatePreConfig = useCallback(
    (index: number, preConfig: Partial<StateProps>) => {
      const op = machineState.orderedOps[index];
      if (!op) return;
      dispatchMachine({ type: "UPDATE_PRE_CONFIG", operation: op, preConfig });
    },
    [machineState, dispatchMachine]
  );

  const toggleExpand = useCallback(
    (index: number) => {
      const op = machineState.orderedOps[index];
      if (!op) return;
      setExpandedOps((prev) => {
        const next = new Set(prev);
        if (next.has(op)) next.delete(op);
        else next.add(op);
        return next;
      });
    },
    [machineState]
  );

  const handleSave = async () => {
    if (!form.nome.trim()) {
      Toast({ title: "Nome do modelo é obrigatório", status: "warning" });
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        nome: form.nome.trim(),
        descricaoCurta: descricaoCurta || undefined,
        icone: form.icone || undefined,
        cor: form.cor || undefined,
        convenio: form.convenio || undefined,
        tipoOperacao: form.tipoOperacao || undefined,
        sequenciaBloco:
          machineState.orderedOps.length > 0
            ? JSON.stringify(machineState.machine.formatToSend())
            : undefined,
        status: form.ativo ? ("ATIVO" as const) : ("INATIVO" as const)
      };

      if (isEditing) {
        await ModelosPipelineEndpoints.updateEstrutura(
          editingModelo!.id!,
          payload
        );
        Toast({ title: "Modelo atualizado com sucesso", status: "success" });
      } else {
        await ModelosPipelineEndpoints.create(payload);
        Toast({ title: "Modelo criado com sucesso", status: "success" });
      }
      onSaved();
    } catch (e: any) {
      Toast({
        title: isEditing ? "Erro ao atualizar modelo" : "Erro ao criar modelo",
        description: e?.response?.data?.message,
        status: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <GetLayout
      title={
        isEditing ? "Editar Modelo de Pipeline" : "Novo Modelo de Pipeline"
      }
      pageTitleProps={{ onClick: onBack }}
      rightContent={
        <Flex gap="8px">
          <Button variant="outline" onClick={onBack} isDisabled={isSaving}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            leftIcon={<CheckIcon />}
            onClick={handleSave}
            isLoading={isSaving}
            loadingText="Salvando"
          >
            Salvar Modelo
          </Button>
        </Flex>
      }
    >
      <Flex gap="20px" align="flex-start">
        {/* Main form */}
        <Flex direction="column" gap="16px" flex={1} minW={0}>
          {/* Informações do Modelo */}
          <SectionCard title="Informações do Modelo" icon={<Text>ℹ️</Text>}>
            <Grid templateColumns="1fr 1fr" gap="16px" mb="16px">
              <FormControl isRequired>
                <FormLabel fontSize="13px" color="#4A5568">
                  Nome do modelo
                </FormLabel>
                <Input
                  placeholder="Ex: INSS Refin HubBank"
                  value={form.nome}
                  onChange={(e) => setField("nome", e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="13px" color="#4A5568">
                  Descrição curta
                </FormLabel>
                <Input
                  value={descricaoCurta}
                  isReadOnly
                  _readOnly={{ cursor: "not-allowed" }}
                  isDisabled={machineState.orderedOps.length === 0}
                />
              </FormControl>
            </Grid>

            <Grid templateColumns="auto 1fr" gap="24px" alignItems="start">
              <FormControl>
                <FormLabel fontSize="13px" color="#4A5568">
                  Ícone
                </FormLabel>
                <Flex gap="6px" flexWrap="wrap" alignItems="center" h="36px">
                  {ICON_OPTIONS.map((opt) => {
                    const selected = form.icone === opt.key;
                    return (
                      <Box
                        key={opt.key}
                        as="button"
                        type="button"
                        w="36px"
                        h="36px"
                        borderRadius="8px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        border="2px solid"
                        borderColor={selected ? "#2F80ED" : "#E2E8F0"}
                        bg={selected ? "#EBF4FF" : "white"}
                        cursor="pointer"
                        onClick={() => setField("icone", opt.key)}
                        _hover={{ borderColor: "#2F80ED" }}
                      >
                        <Icon
                          as={opt.icon}
                          color={selected ? "#2F80ED" : "#718096"}
                          w="16px"
                          h="16px"
                        />
                      </Box>
                    );
                  })}
                </Flex>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="13px" color="#4A5568">
                  Cor de destaque
                </FormLabel>
                <Flex gap="6px" flexWrap="wrap" alignItems="center" h="36px">
                  {COLOR_OPTIONS.map((c) => (
                    <Box
                      key={c}
                      as="button"
                      type="button"
                      w="28px"
                      h="28px"
                      borderRadius="50%"
                      bg={c}
                      border="3px solid"
                      borderColor={form.cor === c ? "#343B44" : "transparent"}
                      cursor="pointer"
                      onClick={() => setField("cor", c)}
                      _hover={{ transform: "scale(1.1)" }}
                      transition="transform 0.1s"
                    />
                  ))}
                </Flex>
              </FormControl>
            </Grid>
          </SectionCard>

          {/* Convênios e Operações */}
          <SectionCard title="Convênios e Operações" icon={<Text>🔗</Text>}>
            <Grid templateColumns="1fr 1fr" gap="20px">
              <FormControl>
                <FormLabel fontSize="13px" color="#4A5568" mb="6px">
                  Convênios
                </FormLabel>
                <Text fontSize="11px" color="#96A2B3" mb="8px">
                  Usado para recomendar o modelo ao abrir o builder.
                </Text>
                <Flex gap="6px" flexWrap="wrap">
                  {mailingConveniosOptions
                    .filter((c) => c.value !== "FGTS")
                    .map((c) => (
                      <TagToggle
                        key={c.value}
                        label={c.name}
                        active={isMultiActive("convenio", c.value)}
                        onClick={() => toggleField("convenio", c.value)}
                      />
                    ))}
                </Flex>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="13px" color="#4A5568" mb="6px">
                  Tipos de Operação
                </FormLabel>
                <Text fontSize="11px" color="#96A2B3" mb="8px">
                  Opcional, para refinar a recomendação.
                </Text>
                <Flex gap="6px" flexWrap="wrap">
                  {dropdownTipoOperacaoMailing.map((op) => (
                    <TagToggle
                      key={op.value}
                      label={op.name}
                      active={isMultiActive("tipoOperacao", op.value)}
                      onClick={() => toggleField("tipoOperacao", op.value)}
                    />
                  ))}
                </Flex>
              </FormControl>
            </Grid>
          </SectionCard>

          {/* Sequência de Blocos */}
          <SectionCard title="Sequência de Blocos" icon={<Text>🔀</Text>}>
            <BlockSequencePanel
              blocks={blocks}
              onAdd={addBlock}
              onRemove={removeBlock}
              onReorder={reorderBlocks}
              onPreConfigChange={updatePreConfig}
              onToggleExpand={toggleExpand}
            />
          </SectionCard>

          {/* Configurações */}
          <SectionCard title="Configurações" icon={<Text>⚙️</Text>}>
            <Flex direction="column" gap="0">
              <Flex
                align="center"
                justify="space-between"
                py="14px"
                borderBottom="1px solid #EDF2F7"
              >
                <Box>
                  <Text fontSize="14px" fontWeight="500" color="#2D3748">
                    Ativo
                  </Text>
                  <Text fontSize="12px" color="#96A2B3">
                    Modelos inativos não aparecem na galeria.
                  </Text>
                </Box>
                <Switch
                  colorScheme="blue"
                  isChecked={form.ativo}
                  onChange={(e) => setField("ativo", e.target.checked)}
                />
              </Flex>
            </Flex>
          </SectionCard>
        </Flex>

        {/* Preview panel */}
        <TemplatePreviewPanel
          icone={form.icone}
          cor={form.cor}
          blocks={blocks}
        />
      </Flex>
    </GetLayout>
  );
}
